<template>
  <div class="leaflet-map leaflet-map--map c-ai-route-map">
    <div ref="map" class="c-ai-route-map__canvas"></div>
    <div class="leaflet-map--map__message" v-if="stops.length === 0 && !start">
      <span>{{ emptyText || $t('weeks.projectplanningexport.map.no_stops') }}</span>
    </div>
    <div class="c-ai-route-map__warning" v-if="routeError">
      {{ $t('weeks.projectplanningexport.map.route_error') }}
    </div>
  </div>
</template>

<script>
import gmapsInit from "../../geocoder/gmaps";
import { START_MARKER_COLOR, WEEKDAY_MARKER_COLORS } from "../weekplanner/aiRouteMapColors";

/**
 * Google Map for one kolonne and one week. `stops` is already ordered and
 * numbered by ProjectPlanningExport.vue. Roads are drawn only while
 * `drawLines` is on, in one Compute Routes request for the whole list.
 *
 * Stop shape: { key, number, lat, lon, day, weekday, address, plannedStart,
 * plannedEnd, projects: [{projectId, projectNumber}] }
 *
 * `start` is the kolonne address from kolonnen_start. It is the route origin
 * and a red S marker. Null means the route begins at the first stop.
 */
export default {
  name: "AiRouteMap",

  props: {
    stops: {
      type: Array,
      default: () => []
    },
    start: {
      type: Object,
      default: null
    },
    zoom: {
      type: Number,
      default: 8
    },
    center: {
      type: Object,
      default: () => ({ lat: 48.9, lng: 9.5 })
    },
    // While false, Compute Routes is not called. Turning it on loads the road.
    drawLines: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      map: null,
      markers: [],
      polylines: [],
      infoWindow: null,
      renderToken: 0,
      routeError: false,
      // One Compute Routes result per stop list, so switching back to a
      // route already drawn does not bill Google again.
      routeCache: {}
    };
  },

  watch: {
    stops() {
      this.renderStops();
    },
    start() {
      this.renderStops();
    },
    drawLines(visible) {
      if (!this.map) {
        return;
      }
      if (!visible) {
        this.applyRouteLines(false);
        return;
      }
      const cached = this.routeCache[this.stopsKey(this.routePoints())];
      if (cached && this.polylines.length > 0) {
        this.applyRouteLines(true);
        return;
      }
      this.renderStops();
    }
  },

  mounted() {
    gmapsInit()
      .then(
        (function() {
          if (this._isDestroyed || !this.$refs.map) {
            return;
          }
          this.initMap();
          this.renderStops();
        }).bind(this)
      )
      .catch(
        (function() {
          this.routeError = true;
        }).bind(this)
      );
  },

  beforeDestroy() {
    this.renderToken += 1;
    this.clearOverlays();
    this.map = null;
  },

  methods: {
    initMap() {
      this.map = new google.maps.Map(this.$refs.map, {
        zoom: this.zoom,
        center: this.center,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });
      this.infoWindow = new google.maps.InfoWindow();
    },

    clearOverlays() {
      this.markers.forEach(function(marker) {
        marker.setMap(null);
      });
      this.markers = [];
      this.polylines.forEach(function(polyline) {
        polyline.setMap(null);
      });
      this.polylines = [];
      if (this.infoWindow) {
        this.infoWindow.close();
      }
    },

    renderStops() {
      if (!this.map) {
        return;
      }

      const token = ++this.renderToken;
      this.clearOverlays();
      this.routeError = false;

      const routePoints = this.routePoints();

      if (routePoints.length === 0) {
        this.map.setCenter(this.center);
        this.map.setZoom(this.zoom);
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      routePoints.forEach(
        function(stop) {
          const position = { lat: stop.lat, lng: stop.lon };
          bounds.extend(position);
          if (stop.role === "start") {
            this.markers.push(this.createStartMarker(stop, position));
            return;
          }
          this.markers.push(this.createMarker(stop, position));
        }.bind(this)
      );
      if (routePoints.length === 1) {
        this.map.setCenter({ lat: routePoints[0].lat, lng: routePoints[0].lon });
        this.map.setZoom(12);
      } else {
        this.map.fitBounds(bounds, 40);
      }

      // Markers are free. The driving line is a billed Routes request, so
      // it waits until the checkbox is on.
      if (!this.drawLines || routePoints.length < 2) {
        return;
      }

      const cacheKey = this.stopsKey(routePoints);
      const cached = this.routeCache[cacheKey];
      if (cached) {
        this.drawCachedLines(cached, bounds);
        this.map.fitBounds(bounds, 40);
        return;
      }

      // Google bills each successful Compute Routes call. One call for the
      // whole ordered list returns every leg, including the hop into the
      // next morning. A call per day, plus another per overnight hop, was
      // a separate bill for the same roads.
      const jobs = this.routeSegments(routePoints).map(
        function(segment) {
          return this.computeRoute(segment, bounds, token);
        }.bind(this)
      );

      Promise.all(jobs)
        .then(
          function(results) {
            if (token !== this.renderToken) {
              return;
            }

            const lines = [];
            let failed = false;
            results.forEach(function(result) {
              if (!result) {
                return;
              }
              if (result.failed) {
                failed = true;
              }
              (result.lines || []).forEach(function(line) {
                lines.push(line);
              });
            });

            if (!failed) {
              this.routeCache[cacheKey] = {
                lines: lines
              };
            }
          }.bind(this)
        )
        .catch(
          function() {
            if (token !== this.renderToken) {
              return;
            }
            this.routeError = true;
          }.bind(this)
        );
    },

    stopsKey(stops) {
      return (stops || [])
        .map(function(stop) {
          return stop.key;
        })
        .join("\n");
    },

    // Ordered points sent to Google. The kolonne start, when it has
    // coordinates, is the origin. Otherwise the list is the stops alone.
    routePoints() {
      const stops = (this.stops || []).filter(function(stop) {
        return Number.isFinite(stop.lat) && Number.isFinite(stop.lon);
      });
      const start = this.normalizedStart();
      if (!start) {
        return stops;
      }
      return [start].concat(stops);
    },

    normalizedStart() {
      const start = this.start;
      if (!start) {
        return null;
      }
      const lat = Number(start.lat);
      const lon = Number(start.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return null;
      }
      return {
        role: "start",
        key: start.key || "start|" + lat + "|" + lon,
        lat: lat,
        lon: lon,
        address: start.address || {}
      };
    },

    computeRoute: async function(dayStops, bounds, token) {
      const lines = [];
      if (dayStops.length < 2) {
        return { lines: lines, failed: false };
      }

      let Route;
      try {
        const routesLibrary = await google.maps.importLibrary("routes");
        Route = routesLibrary.Route;
      } catch (error) {
        if (token === this.renderToken) {
          this.routeError = true;
        }
        return { lines: lines, failed: true };
      }

      if (token !== this.renderToken || !this.map) {
        return { lines: lines, failed: false };
      }

      const segment = dayStops;
      const intermediates = segment.slice(1, -1).map(function(stop) {
        return {
          location: { lat: stop.lat, lng: stop.lon }
        };
      });
      const request = {
        origin: { lat: segment[0].lat, lng: segment[0].lon },
        destination: {
          lat: segment[segment.length - 1].lat,
          lng: segment[segment.length - 1].lon
        },
        travelMode: "DRIVING",
        optimizeWaypointOrder: false,
        // `path` and `legs` come back on this same billed request. Each
        // same-day hop is drawn from leg.path. A dotted field such as
        // legs.distanceMeters is rejected.
        fields: ["path", "legs"]
      };
      if (intermediates.length > 0) {
        request.intermediates = intermediates;
      }

      let failed = false;
      try {
        const result = await Route.computeRoutes(request);
        if (token !== this.renderToken || !this.map) {
          return { lines: lines, failed: false };
        }

        const route = result && result.routes && result.routes[0];
        if (!route) {
          this.routeError = true;
          return { lines: lines, failed: true };
        }

        const routeLegs = route.legs || [];
        if (routeLegs.length !== segment.length - 1) {
          failed = true;
        }
        routeLegs.forEach(
          function(leg, legIndex) {
            const fromStop = segment[legIndex];
            const toStop = segment[legIndex + 1];
            if (!fromStop || !toStop) {
              return;
            }
            // The kolonne start has no weekday. Its leg is the departure
            // to the first stop. A hop onto a later day is the drive from
            // that day's last stop to the next day's first stop.
            const color = fromStop.role === "start"
              ? START_MARKER_COLOR
              : WEEKDAY_MARKER_COLORS[fromStop.weekday] || WEEKDAY_MARKER_COLORS[1];
            const points = this.drawLegLine(leg, route, color, bounds);
            if (!points) {
              this.routeError = true;
              failed = true;
            } else {
              lines.push({
                path: points,
                color: color
              });
            }
          }.bind(this)
        );
        this.map.fitBounds(bounds, 40);
      } catch (error) {
        if (token === this.renderToken) {
          this.routeError = true;
          failed = true;
        }
      }

      return { lines: lines, failed: failed };
    },

    drawCachedLines(cached, bounds) {
      (cached.lines || []).forEach(
        function(line) {
          if (!line || !line.path || line.path.length < 2) {
            return;
          }
          const polyline = new google.maps.Polyline({
            path: line.path,
            strokeColor: line.color,
            strokeWeight: 4,
            strokeOpacity: 0.85,
            map: this.map
          });
          this.polylines.push(polyline);
          line.path.forEach(function(point) {
            bounds.extend(point);
          });
        }.bind(this)
      );
    },

    snapshotPath(path) {
      const points = [];
      if (!path) {
        return points;
      }
      const list = typeof path.getArray === "function" ? path.getArray() : path;
      (Array.isArray(list) ? list : []).forEach(function(point) {
        if (!point) {
          return;
        }
        const lat = typeof point.lat === "function" ? point.lat() : point.lat;
        const lng = typeof point.lng === "function" ? point.lng() : point.lng;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          points.push({ lat: lat, lng: lng });
        }
      });
      return points;
    },

    drawLegLine(leg, route, color, bounds) {
      let path = leg && leg.path;
      if ((!path || path.length < 2) && route && route.path && route.path.length > 1 && route.legs && route.legs.length === 1) {
        path = route.path;
      }
      const points = this.snapshotPath(path);
      if (points.length < 2) {
        return null;
      }

      const polyline = new google.maps.Polyline({
        path: points,
        strokeColor: color,
        strokeWeight: 4,
        strokeOpacity: 0.85,
        map: this.drawLines ? this.map : null
      });
      this.polylines.push(polyline);
      points.forEach(function(point) {
        bounds.extend(point);
      });
      return points;
    },

    applyRouteLines(visible) {
      if (!this.map) {
        return;
      }

      this.polylines.forEach(
        function(polyline) {
          polyline.setMap(visible ? this.map : null);
        }.bind(this)
      );
    },

    routeSegments(stops) {
      // 25 intermediate stops is the API maximum, so a longer list is split
      // into overlapping chunks. Fewer chunks means fewer billed requests.
      const maxPoints = 27;
      if (stops.length <= maxPoints) {
        return [stops];
      }

      const segments = [];
      for (let index = 0; index < stops.length - 1; ) {
        const end = Math.min(index + maxPoints, stops.length);
        segments.push(stops.slice(index, end));
        index = end - 1;
      }
      return segments;
    },

    createStartMarker(start, position) {
      const marker = new google.maps.Marker({
        map: this.map,
        position: position,
        zIndex: 1000,
        label: {
          text: "S",
          color: "#ffffff",
          fontSize: "16px",
          fontWeight: "700"
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 18,
          fillColor: START_MARKER_COLOR,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2
        }
      });

      marker.addListener(
        "click",
        function() {
          this.infoWindow.setContent(this.startPopupHtml(start));
          this.infoWindow.open(this.map, marker);
        }.bind(this)
      );

      return marker;
    },

    startPopupHtml(start) {
      const address = start.address || {};
      const streetLine = [address.street, address.housenumber].filter(Boolean).join(" ");
      const cityLine = [address.zipcode, address.city].filter(Boolean).join(" ");

      return (
        '<div class="c-ai-route-map__popup">' +
        "<strong>" + this.escapeHtml(this.$t("weeks.projectplanningexport.map.start_point")) + "</strong><br>" +
        this.escapeHtml(streetLine) + "<br>" +
        this.escapeHtml(cityLine) +
        "</div>"
      );
    },

    createMarker(stop, position) {
      const color = WEEKDAY_MARKER_COLORS[stop.weekday] || WEEKDAY_MARKER_COLORS[1];
      const marker = new google.maps.Marker({
        map: this.map,
        position: position,
        label: {
          text: String(stop.number),
          color: "#ffffff",
          fontSize: "11px",
          fontWeight: "700"
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 1
        }
      });

      marker.addListener(
        "click",
        function() {
          this.infoWindow.setContent(this.popupHtml(stop));
          this.infoWindow.open(this.map, marker);
        }.bind(this)
      );

      return marker;
    },

    escapeHtml(value) {
      const div = document.createElement("div");
      div.innerText = value === null || value === undefined ? "" : String(value);
      return div.innerHTML;
    },

    popupHtml(stop) {
      const when = this.$moment(stop.plannedStart);
      const timeLabel = when.format("dddd");
      const address = stop.address || {};
      const streetLine = [address.street, address.housenumber].filter(Boolean).join(" ");
      const cityLine = [address.zipcode, address.city].filter(Boolean).join(" ");
      const projectLines = (stop.projects || [])
        .map(
          function(project) {
            return "<div>" + this.escapeHtml(project.projectNumber) + "</div>";
          }.bind(this)
        )
        .join("");

      return (
        '<div class="c-ai-route-map__popup">' +
        "<strong>" + this.escapeHtml(timeLabel) + " (" + this.escapeHtml(stop.number) + ")</strong><br>" +
        this.escapeHtml(streetLine) + "<br>" +
        this.escapeHtml(cityLine) +
        '<div class="u-small-margin--top">' + projectLines + "</div>" +
        "</div>"
      );
    }
  }
};
</script>
